import { useEffect, useState } from 'react';
import Masonry from 'react-layout-masonry';
import axios from 'axios';
import './Snippet.css';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from "@phosphor-icons/react";

const SnippetCard = ({ title, id, language, solution }) => {
  useEffect(() => {
    hljs.highlightAll();
  }, []);

  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/snippet/${id}`);
  };

  return (
    <div className="carve-card" onClick={handleCardClick}>
      <div className="carve-card-head">
        <div className="heading-medium snippet-title">
          {title.length > 70 ? title.substring(0, 70) + "..." : title}
        </div>
      </div>
      <div className="carve-card-body">
        <pre>
          <code className="language-javascript">
            {solution.length > 50 ? solution.substring(0, 50) + "..." : solution}
          </code>
        </pre>
      </div>
      <div className="carve-card-footer">
        <div className="carve-chips-group">
          <div className="carve-chips">
            <span>{language}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Snippet = () => {
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState(4);

  const updateColumns = () => {
    const width = window.innerWidth;
    if (width > 1200) {
      setColumns(4);
    } else if (width > 992) {
      setColumns(3);
    } else if (width > 768) {
      setColumns(2);
    } else {
      setColumns(1);
    }
  };

  useEffect(() => {
    const fetchSnippets = async () => {
      try {
        const response = await axios.get('https://carve.enminvithaigal.in/lab/Apicall.php', {
        // const response = await axios.get('http://106.51.127.87:3030/LabManual/Apicall.php', {
          params: { Apicall: 'getSnippets' },
        });

        if (Array.isArray(response.data)) {
          setSnippets(response.data);
        } else if (response.data.error) {
          setError(response.data.error);
        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching snippets:', err);
        setError('Failed to fetch snippets.');
      } finally {
        setLoading(false);
      }
    };

    fetchSnippets();
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => {
      window.removeEventListener('resize', updateColumns);
    };
  }, []);

  // --- Search ---
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();

    if (term.trim() === "") {
      setSearchResults([]);
      return;
    }

    const filteredResults = snippets.filter((snippet) =>
      snippet.program.toLowerCase().includes(term) ||
      snippet.language_name.toLowerCase().includes(term)
    );

    setSearchResults(filteredResults);
  }, [searchTerm, snippets]);

  const handleClear = () => {
    setSearchTerm("");
    setSearchResults([]);
    closeDrawer();
  };

  const searchSnippets = () => {
    var drawerseek = document.getElementsByClassName('snippet-drawer')[0];
    drawerseek.classList.add('seek');
    document.body.classList.add('freeze');
  }

  const closeDrawer = () => {
    const drawerseek = document.querySelector('.snippet-drawer');
    const drawerChild = drawerseek.children[0];
    drawerChild.classList.add('drawer-hide');
    drawerseek.classList.add('waneout');
    setTimeout(() => {
      drawerseek.classList.remove('seek');
      drawerChild.classList.remove('drawer-hide');
      drawerseek.classList.remove('waneout');
      drawerseek.style.display = 'none';
      document.body.classList.remove('freeze');
    }, 290);
  };

  return (
    <div className="snippet-card-group">
      <button className="component_search" onClick={searchSnippets} aria-label="Search">
        <MagnifyingGlassIcon
          size={32}
          weight="regular"

          className="icon snip-search"
        />
      </button>
      <motion.div
        initial={{ x: 10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -10, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >

        {/* Search Icon + Drawer */}
        <div style={{ marginBottom: '3rem' }}>


          <div className="carve-drawer search-drawer snippet-drawer">
            <div className="carve-drawer-inner">
              <div className="carve-drawer-head">
                <div className="heading-medium">Search Code</div>
                <button className="icon icon-close" onClick={closeDrawer}>
                  &times;
                </button>
              </div>
              <div className="carve-drawer-body">
                <div className="search-group snippet-search" id="searchSnippets">
                  <svg
                    className="icon icon-search"
                    width="24"
                    height="24"
                    viewBox="0 0 256 256"
                  >
                    <path d="M232.49,215.51,185,168a92.12,92.12,0,1,0-17,17l47.53,47.54a12,12,0,0,0,17-17ZM44,112a68,68,0,1,1,68,68A68.07,68.07,0,0,1,44,112Z"></path>
                  </svg>
                  <input
                    type="text"
                    required
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                  />
                  {searchResults.length > 0 && (
                    <ul className="search-results carve-list">
                      {searchResults.map((result) => (
                        <li key={result.solution_id}>
                          <Link
                            to={`/snippet/${result.solution_id}`}
                            onClick={handleClear}>
                            {result.program} - {result.language_name}
                            {/* <span style={{ fontSize: "12px", color: "#150909ff" }}>
                              ({result.language_name})
                            </span> */}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Snippet Masonry Grid */}
        <Masonry
          gap={16}
          columns={columns}
          style={{ width: '100%', margin: '0 auto', boxSizing: 'border-box' }}
        >
          {(searchTerm ? searchResults : snippets).map((snippet) => (
            <SnippetCard
              key={snippet.solution_id}
              id={snippet.solution_id}
              title={snippet.program}
              language={snippet.language_name}
              solution={snippet.solution}
            />
          ))}
        </Masonry>
      </motion.div>
    </div>
  );
};

export default Snippet;
